import {
  CentraCheckout,
  CentraCheckoutFlowProps,
  useCentraSelectionResponse,
} from "@made-people/grease-centra";
import { useHref, useLink } from "@made-people/grease-link";
import { CartSummary } from "@made-people/blueprint-cart-summary";
import {
  ShippingMethodsNotConfigured,
  AddressForm,
  PaymentWrapper,
} from "@made-people/blueprint-checkout";
import { MMShippingMethodOption } from "../components/blueprint/checkout/default-props";
import { MMCentraShippingMethodsRenderer } from "../components/checkout/MMCentraShippingMethodsRenderer";
import { MMPaymentWrapper } from "../components/checkout/MMPaymentWrapper";
import { useTranslate } from "@made-people/grease-translations";
import { MissMaryLogo } from "../assets/icons/MissMaryLogo";
import { ArrowLeft } from "../assets/icons/ArrowLeft";
import { FrontendContextV1 } from "@made-people/nestjs-bff-service";
import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useCartState } from "@made-people/grease-cart";
import { Button } from "@made-people/blueprint-button";
import { BffClient } from "@made-people/gears-bff-client";
import {
  useGearsServiceUrl,
  useGearsConfigurationVariable,
} from "@made-people/gears-configuration";
import { usePaymentMethodCode } from "../hooks/use-payment-method-code";
import { useGeneralSettings } from "../hooks/use-general-settings";
import { SbBlokData } from "@storyblok/js";
import { StoryblokComponent } from "@made-people/grease-storyblok";
import { MMFreeShippingBanner } from "../components/global/MMFreeShippingBanner";
import { MMStandalonePaymentButtons } from "../components/checkout/MMStandalonePaymentButtons";
import { TrustpilotReviews } from "../components/features/trustpilot/components/trustpilot-reviews";
import { useGreaseGrowthbookFeatureValue } from "@made-people/grease-growthbook";
import { useCountriesAndLanguages } from "@made-people/grease-countries-and-languages";

const StandalonePaymentWrapper = (props: PropsWithChildren) => {
  return <div className="relative -z-0">{props.children}</div>;
};

export const CheckoutIndexRoute = (props: {
  baseUrl: string;
  frontendContext: FrontendContextV1;
}) => {
  const href = useHref();
  const Link = useLink();
  const translate = useTranslate();
  const cartState = useCartState();
  const selectionResponse = useCentraSelectionResponse();
  const bffUrl = useGearsServiceUrl("bff");
  const tenantId = useGearsConfigurationVariable("tenantId");
  const paymentMethodCode = usePaymentMethodCode(props.frontendContext);
  const generalSettings = useGeneralSettings();
  const { countries } = useCountriesAndLanguages();

  const checkoutInfoBlocks = generalSettings?.checkoutInformationsBlocks?.map(
    (component: SbBlokData, index: number) => (
      <StoryblokComponent key={index} blok={component} />
    ),
  );

  const checkoutUpsellComponents =
    generalSettings?.checkoutUpsellComponents?.map(
      (component: SbBlokData, index: number) => (
        <StoryblokComponent key={index} blok={component} />
      ),
    );

  const paypalBelowKco = useGreaseGrowthbookFeatureValue(
    "checkout:paypal-stripe-below-kco",
    false,
  );

  const [baseUrl, setBaseUrl] = useState<string | undefined>(undefined);

  useEffect(() => {
    setBaseUrl(window.location.origin ?? props.baseUrl);
  }, []);

  const checkoutIsBlocked = useMemo(() => {
    if (!selectionResponse) {
      return true;
    }

    const currentCountryCode = selectionResponse.location.country;

    const currentCountry = countries.find(
      (country) => country.countryCode === currentCountryCode,
    );

    return !currentCountry;
  }, [selectionResponse, countries]);

  const additionalNotesGetter = useCallback(() => {
    const cartId = cartState.cart?.cartId;
    if (cartId) {
      return {
        // This is used to verify the server-side GA purchase push that
        // the old backend handles
        checkoutToken: cartId,
      };
    }
    return undefined;
  }, [cartState.cart]);

  const visitorIpAddress = () =>
    new BffClient({ baseUrl: bffUrl, tenantId })
      .getUserMeta()
      .then((res) => res?.ipAddress);

  if (!baseUrl) {
    return null;
  }

  const emptyCart =
    selectionResponse && selectionResponse.selection.items.length === 0;

  const flow: CentraCheckoutFlowProps = {
    singlePaymentMethodFlowWithBlocking: {
      paymentInitialize: {
        paymentMethod: paymentMethodCode,
      },
      checkoutIsBlocked,
    },
  };

  if (!paypalBelowKco) {
    flow.singlePaymentMethodFlowWithBlocking!.beforeContent =
      MMStandalonePaymentButtons;
  } else {
    flow.singlePaymentMethodFlowWithBlocking!.afterContent =
      MMStandalonePaymentButtons;
  }

  return (
    <section className="bg-white relative">
      <div className="px-4 justify-between flex py-4 border-border-light border-b border-solid items-center">
        <Link
          className="text-label-md lg:text-label-lg text-left flex group items-center"
          href=""
        >
          <ArrowLeft className="w-4 h-auto mr-2" />
          {translate("Checkout_Go_Back")}
        </Link>
        <Link href="" className="hover:-translate-y-1 transition-transform">
          <MissMaryLogo className="w-20" />
        </Link>
        <span className="text-label-md lg:text-label-lg font-bold">
          {translate("Checkout_Header_Your_Order")}
        </span>
      </div>
      <div className="px-4 md:mx-auto md:max-w-component-max-w-lg min-h-[100svh] lg:grid lg:grid-cols-[1fr_380px] lg:px-10 grid-rows-[min-content_1fr] lg:gap-x-24 pb-20">
        {emptyCart ? (
          <div className="col-span-2 flex justify-center items-center flex-col gap-6 h-full mt-20">
            <span className="text-heading-md lg:text-heading-xl">
              {translate("Checkout_EmptyState_Message")}
            </span>
            <Button
              as="a"
              link={{
                href: "",
              }}
              label={translate("Checkout_EmptyState_Button")}
            />
          </div>
        ) : (
          <div className="lg:col-start-2 lg:col-end-3 lg:row-start-2 lg:row-end-3 mt-4 lg:mt-10 border-surface-1 lg:border lg:p-6 lg:h-fit lg:rounded-md">
            <MMFreeShippingBanner />
            <CartSummary
              templateProps={{
                template: "birch",
                variants: {
                  heading: "hide",
                },
              }}
            />

            {checkoutUpsellComponents && (
              <div
                className="hidden lg:flex flex-col mt-4 lg:max-w-[700px]"
                data-in-upsell="true"
              >
                {checkoutUpsellComponents}
              </div>
            )}
          </div>
        )}
        <div className="lg:col-start-1 lg:col-end-2 lg:row-start-2 lg:row-end-3 mt-4 lg:mt-10">
          {!emptyCart && checkoutInfoBlocks}
          {checkoutUpsellComponents && (
            <div className="flex lg:hidden flex-col mt-4" data-in-upsell="true">
              {checkoutUpsellComponents}
            </div>
          )}
          {cartState.cart && (
            <CentraCheckout
              uiConfig={{
                components: {
                  ShippingMethodsNotConfigured,
                  ShippingMethodOption: MMShippingMethodOption,
                  StandalonePaymentWrapper,
                  AddressForm,
                  CentraShippingMethodsRenderer:
                    MMCentraShippingMethodsRenderer,
                  KlarnaCheckoutWrapper: MMPaymentWrapper,
                  AdyenDropInWrapper: PaymentWrapper,
                },
              }}
              flow={flow}
              paymentPages={{
                paymentReturnPage: baseUrl + href("checkout/finalize"),
                paymentFailedPage: baseUrl + href("checkout"),
                additionalNotesGetter,
                visitorIpAddress,
                sendShippingAddressToPaymentFields: true,
              }}
            />
          )}
        </div>
      </div>
      <div className="mb-20">
        <TrustpilotReviews type="store" />
      </div>
    </section>
  );
};
